/**
 * <anchor-nav> — Generic sticky anchor navigation web component
 *
 * Usage:
 *   <anchor-nav class="anchor-nav__scroll"
 *               data-header-selector=".t4s-section-header"
 *               data-main-selector="#MainContent"
 *               data-root-margin-bottom="-60%">
 *     <nav class="anchor-nav__items" aria-label="Section navigation">
 *       <a href="#section-1" class="anchor-nav__item active" aria-current="true">Label 1</a>
 *       <a href="#section-2" class="anchor-nav__item">Label 2</a>
 *     </nav>
 *   </anchor-nav>
 *
 * Required CSS classes:
 *   .anchor-nav        — closest wrapper element (handles sticky + stuck states)
 *   .anchor-nav__items — scroll container for items
 *   .anchor-nav__item  — each nav link
 *
 * Events dispatched:
 *   'anchor-nav:change' on the element, detail: { index, item, id }
 *
 * Public API (instance methods):
 *   .setActive(index)      — programmatically set active item
 *   .scrollToIndex(index)  — scroll page to the target of an item
 */
(function () {
  if (customElements.get('anchor-nav')) return;

  const SELECTOR_ITEM = '.anchor-nav__item';
  const SELECTOR_ITEMS = '.anchor-nav__items';
  const SELECTOR_SECTION = '.anchor-nav';

  class AnchorNav extends HTMLElement {
    constructor() {
      super();
      this.navItems = [];
      this.activeIndex = 0;
      this.isScrolling = false;
      this.observer = null;
      this._stickyObserver = null;
      this._scrollEndTimer = null;
    }

    connectedCallback() {
      this.navItems = [...this.querySelectorAll(SELECTOR_ITEM)];
      this.navWrapper = this.querySelector(SELECTOR_ITEMS);
      if (!this.navItems.length || !this.navWrapper) return;

      this.sectionEl = this.closest(SELECTOR_SECTION);
      this.headerSelector = this.dataset.headerSelector || '.t4s-section-header';
      this.mainSelector = this.dataset.mainSelector || '#MainContent';
      this.rootMarginBottom = this.dataset.rootMarginBottom || '-60%';
      this.scrollBehavior = this.dataset.scrollBehavior === 'auto' ? 'auto' : 'smooth';

      this._ensureSlider();
      this.updateSlider(this.navItems[this.activeIndex]);

      this._bindClick();
      this._bindGlobalScroll();
      this._bindResize();
      this._bindHeaderEvents();
      this._initStickyDetect();
      this._initBottomMode();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._initObserver(), { once: true });
      } else {
        this._initObserver();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this._stickyObserver?.disconnect();
      if (this._handleBottomMode) document.removeEventListener('scroll', this._handleBottomMode);
      if (this._handleGlobalScroll) document.removeEventListener('scroll', this._handleGlobalScroll);
      if (this._handleResize) window.removeEventListener('resize', this._handleResize);
      if (this._handleHeaderResize) window.removeEventListener('resize', this._handleHeaderResize);
      if (this._sentinel) this._sentinel.remove();
    }

    /* ---------- public ---------- */

    setActive(index) {
      if (index < 0 || index >= this.navItems.length) return;
      const next = this.navItems[index];
      if (index === this.activeIndex && next?.classList.contains('active')) return;
      this.navItems.forEach((item) => {
        item.classList.remove('active');
        item.removeAttribute('aria-current');
      });
      this.activeIndex = index;
      next?.classList.add('active');
      next?.setAttribute('aria-current', 'true');
      this.updateSlider(next);
      this.dispatchEvent(
        new CustomEvent('anchor-nav:change', {
          detail: { index, item: next, id: next?.getAttribute('href')?.substring(1) },
          bubbles: true,
        })
      );
    }

    scrollToIndex(index) {
      const item = this.navItems[index];
      const id = item?.getAttribute('href')?.substring(1);
      const el = id ? document.getElementById(id) : null;
      if (!el) return;
      this.isScrolling = true;
      this.setActive(index);
      this._centerActiveItem();
      this._headerEl?.dispatchEvent(new Event('preventHeaderReveal'));
      const offset = item.hasAttribute('data-no-offset') ? 0 : this._getOffset();
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - offset,
        behavior: this.scrollBehavior,
      });
    }

    /* ---------- internals ---------- */

    _ensureSlider() {
      let slider = this.navWrapper.querySelector('.anchor-nav__slider');
      if (!slider) {
        slider = document.createElement('div');
        slider.classList.add('anchor-nav__slider');
        this.navWrapper.appendChild(slider);
      }
      this.slider = slider;
    }

    _getOffset() {
      if (!this.sectionEl) return 0;
      return this.sectionEl.offsetHeight + (parseInt(getComputedStyle(this.sectionEl).top, 10) || 0);
    }

    updateSlider(button) {
      if (!button || !this.slider || !this.navWrapper) return;
      const wrapperWidth = this.navWrapper.offsetWidth;
      if (!wrapperWidth) return;
      const left = ((button.offsetLeft / wrapperWidth) * 100).toFixed(2);
      const right = (100 - ((button.offsetLeft + button.offsetWidth) / wrapperWidth) * 100).toFixed(2);
      this.slider.style.clipPath = `inset(0 ${right}% 0 ${left}%)`;
    }

    _centerActiveItem() {
      const active = this.navItems[this.activeIndex];
      if (!active) return;
      const target = active.offsetLeft - (this.clientWidth - active.offsetWidth) / 2;
      this.scrollTo({ left: Math.max(0, target), behavior: this.scrollBehavior });
    }

    _initObserver() {
      this.observer?.disconnect();
      const offset = this._getOffset();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (this.isScrolling) return;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const index = this.navItems.findIndex((i) => i.getAttribute('href') === `#${entry.target.id}`);
            if (index === -1) continue;
            this.setActive(index);
            this._centerActiveItem();
          }
        },
        { rootMargin: `-${offset}px 0px ${this.rootMarginBottom} 0px`, threshold: 0 }
      );
      this.navItems.forEach((item) => {
        const id = item.getAttribute('href')?.substring(1);
        const el = id ? document.getElementById(id) : null;
        if (el) this.observer.observe(el);
      });
    }

    _bindClick() {
      this.navWrapper.addEventListener('click', (e) => {
        const item = e.target.closest(SELECTOR_ITEM);
        if (!item) return;
        const index = this.navItems.indexOf(item);
        if (index === -1) return;
        e.preventDefault();
        this.scrollToIndex(index);
      });
    }

    _bindGlobalScroll() {
      this._handleGlobalScroll = () => {
        if (!this.isScrolling) return;
        clearTimeout(this._scrollEndTimer);
        this._scrollEndTimer = setTimeout(() => {
          this.isScrolling = false;
        }, 150);
      };
      document.addEventListener('scroll', this._handleGlobalScroll, { passive: true });
    }

    _bindResize() {
      this._handleResize = () => {
        this._initObserver();
        this.updateSlider(this.navItems[this.activeIndex]);
      };
      window.addEventListener('resize', this._handleResize, { passive: true });
    }

    _bindHeaderEvents() {
      this._headerEl = document.querySelector(this.headerSelector);
      const readHeaderHeight = () => {
        this.headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0px';
      };
      if (window.luteUtil?.lazyLoad) {
        window.luteUtil.lazyLoad(readHeaderHeight);
      } else {
        readHeaderHeight();
      }

      if (!this._headerEl || !this.sectionEl) return;
      this._headerEl.addEventListener('T4sHeaderHide', () => {
        this.sectionEl.style.setProperty('--sticky-top', '0px');
        requestAnimationFrame(() => {
          this._initObserver();
          this._refreshBottomModeCache?.();
        });
      });
      this._headerEl.addEventListener('T4sHeaderReveal', () => {
        this.sectionEl.style.setProperty('--sticky-top', this.headerHeight);
        requestAnimationFrame(() => {
          this._initObserver();
          this._refreshBottomModeCache?.();
        });
      });
      this._handleHeaderResize = readHeaderHeight;
      window.addEventListener('resize', this._handleHeaderResize, { passive: true });
    }

    _initStickyDetect() {
      if (!this.sectionEl) return;
      const sentinel = document.createElement('div');
      sentinel.style.cssText = 'height:0;width:0;pointer-events:none;visibility:hidden';
      this.sectionEl.parentNode.insertBefore(sentinel, this.sectionEl);
      this._sentinel = sentinel;
      this._stickyObserver = new IntersectionObserver(
        ([entry]) => {
          this.sectionEl.classList.toggle('is-stuck', !entry.isIntersecting);
        },
        { threshold: 0 }
      );
      this._stickyObserver.observe(sentinel);
    }

    _initBottomMode() {
      this.isFixedTop = false;
      const mainContent = document.querySelector(this.mainSelector);
      if (!mainContent || !this.sectionEl) return;

      // Cache layout-dependent values; refresh only on resize / header events.
      let navHeight = this.sectionEl.offsetHeight;
      let stickyTop = parseInt(getComputedStyle(this.sectionEl).getPropertyValue('--sticky-top'), 10) || 0;
      const refreshCache = () => {
        navHeight = this.sectionEl.offsetHeight;
        stickyTop = parseInt(getComputedStyle(this.sectionEl).getPropertyValue('--sticky-top'), 10) || 0;
      };
      this._refreshBottomModeCache = refreshCache;

      let rafId = 0;
      this._handleBottomMode = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          const rect = mainContent.getBoundingClientRect();
          const shouldPin = rect.bottom < stickyTop + navHeight;
          if (shouldPin !== this.isFixedTop) {
            this.isFixedTop = shouldPin;
            this.sectionEl.classList.toggle('is-fixed-top', shouldPin);
          }
        });
      };
      document.addEventListener('scroll', this._handleBottomMode, { passive: true });
      window.addEventListener('resize', refreshCache, { passive: true });
      this._handleBottomMode();
    }
  }

  customElements.define('anchor-nav', AnchorNav);
})();

/**
 * <task-event-tracker>
 * Shopify 商城完成任务事件上报组件
 *
 * 接口：POST /api/station/activity/task/shopify/taskEventReport
 * 请求体：{ email?, url?, eventType?, orderId? }
 *
 * 用法：
 *   <task-event-tracker data-customer="{{ customer.id }}" data-page-view="view_home"></task-event-tracker>
 *
 *   window.taskEventTracker.report('place_order', { orderId, email });
 *
 * 自动行为：
 *   - 必须带 data-customer（Shopify 登录态），未登录时组件不激活，不绑定任何监听
 *   - 元素带 data-page-view：挂载后自动上报该 eventType（页面浏览）
 *   - 监听 ATC 点击 + 主题 `add:cart:success` 事件，加购成功后上报 data-cart-event-type（默认 'add_your_favorite_to_cart'）
 */
(function () {
  if (customElements.get('task-event-tracker')) return;
  const BASE_URL = window.luteUtil?.getApiUrl()
  const DEFAULT_ENDPOINT = `${BASE_URL}/api/station/activity/task/shopify/taskEventReport`;

  class TaskEventTracker extends HTMLElement {
    #atcHandler = null;
    #cartSuccessHandler = null;
    #loginHandler = null;
    #pendingCart = false;
    #pendingReports = [];

    connectedCallback() {
      if (!this.dataset.customer) return;
      if (!window.__taskEventTrackerListenerAttached) {
        this.#initAtcTracking();
        window.__taskEventTrackerListenerAttached = true;
      }
      if (!window.taskEventTracker) {
        window.taskEventTracker = {
          report: (eventType, extra) => this.report(eventType, extra),
          instance: this,
        };
      }
      this.#loginHandler = () => this.#flushPending();
      document.addEventListener('LUTE:AUTH:LOGIN', this.#loginHandler);
      this.#reportPageView();
    }

    disconnectedCallback() {
      if (this.#atcHandler) {
        document.removeEventListener('click', this.#atcHandler, true);
        this.#atcHandler = null;
      }
      if (this.#cartSuccessHandler) {
        const $ = window.jQuery_T4NT || window.jQuery;
        $?.(document).off('add:cart:success', this.#cartSuccessHandler);
        this.#cartSuccessHandler = null;
      }
      if (this.#loginHandler) {
        document.removeEventListener('LUTE:AUTH:LOGIN', this.#loginHandler);
        this.#loginHandler = null;
      }
      window.__taskEventTrackerListenerAttached = false;
      if (window.taskEventTracker?.instance === this) {
        delete window.taskEventTracker;
      }
    }

    get isLoggedIn() {
      return !!(window.__MOMCOZY_USER_INFO__ && window.__MOMCOZY_USER_INFO__.email);
    }

    #flushPending() {
      if (!this.isLoggedIn || !this.#pendingReports.length) return;
      const queue = this.#pendingReports.splice(0);
      queue.forEach(({ eventType, extra }) => this.report(eventType, extra));
    }

    get endpoint() {
      return this.dataset.endpoint || DEFAULT_ENDPOINT;
    }

    get email() {
      return this.dataset.email || window?.__MOMCOZY_USER_INFO__?.email;
    }

    report(eventType, extra = {}) {
      if (!eventType) return;
      if (!this.isLoggedIn) {
        this.#pendingReports.push({ eventType, extra });
        return;
      }
      const body = {
        eventType,
        url: extra.url ?? window.location.href,
        email: extra.email ?? this.email ?? undefined,
        orderId: extra.orderId ?? undefined,
      };
      return this.#send(body);
    }

    #send(body) {
      return fetch(this.endpoint, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.__MOMCOZY_USER_INFO__?.token || ''}`,
        },
        body: JSON.stringify(body),
      }).catch((err) => {
        console.warn('[task-event-tracker] report failed:', err);
      });
    }

    #reportPageView() {
      const eventType = this.dataset.pageView;
      if (!eventType) return;
      this.report(eventType);
    }

    /**
     * 监听 ATC 点击 → 暂存待上报标记；
     * 主题在加购请求成功后会触发 jQuery `add:cart:success`，到时才真正上报。
     */
    #initAtcTracking() {
      this.#atcHandler = (e) => {
        const target = e.target;
        if (target.closest('[data-action-atc], [data-atc-form]')) {
          this.#pendingCart = true;
        }
      };
      document.addEventListener('click', this.#atcHandler, true);

      const $ = window.jQuery_T4NT || window.jQuery;
      if (!$) return;
      this.#cartSuccessHandler = () => {
        if (!this.#pendingCart) return;
        this.#pendingCart = false;
        const cartEventType = 2
        this.report(cartEventType);
      };
      $(document).on('add:cart:success', this.#cartSuccessHandler);
    }
  }

  customElements.define('task-event-tracker', TaskEventTracker);
})();